<template>
	<el-dialog
		:title="'设备检测'"
		:visible.sync="dialogVisible"
		:width="isMobile ? 'calc(100% - 24px)' : '600px'"
		custom-class="device-check-dialog-shell"
		:close-on-click-modal="false"
		append-to-body
		@open="onOpen"
		@close="stopTests"
	>
		<div class="device-check-dialog">
			<div class="device-check-intro">
				<div class="device-check-intro__icon"><i class="el-icon-headset"></i></div>
				<div class="device-check-intro__copy">
					<div class="device-check-intro__title">通话设备</div>
					<div class="device-check-intro__text">确认摄像头和麦克风可用，选择会应用到下一次通话。</div>
				</div>
				<el-button size="mini" plain :loading="loading" @click="refreshDevices(true)">授权并刷新</el-button>
			</div>
			<div v-if="unsupported" class="device-check-empty">当前环境不支持设备检测，请使用安全连接或桌面客户端。</div>
			<template v-else>
				<div class="device-check-summary" :class="{ 'is-error': statusType === 'error' }">
					<i :class="statusType === 'error' ? 'el-icon-warning-outline' : 'el-icon-circle-check'"></i>
					<span>{{ statusText }}</span>
				</div>
				<div class="device-card">
					<div class="device-card__icon device-card__icon--camera"><i class="el-icon-camera"></i></div>
					<div class="device-card__body">
						<div class="device-card__title">摄像头 <span v-if="selectedVideoId" class="device-card__tag">已选择</span></div>
						<div class="device-card__hint">用于视频通话和本地预览</div>
						<el-select v-model="selectedVideoId" class="device-select" size="small" placeholder="使用系统默认设备" @change="saveSelection('video', selectedVideoId)">
							<el-option v-for="device in videoOptions" :key="device.deviceId" :label="device.label" :value="device.deviceId"></el-option>
						</el-select>
					</div>
					<el-button class="device-card__action" size="mini" plain :disabled="!videoDevices.length || (testing && !cameraTesting)" @click="testCamera">{{ cameraTesting ? '停止测试' : '测试' }}</el-button>
				</div>
				<div class="device-card">
					<div class="device-card__icon device-card__icon--audio"><i class="el-icon-microphone"></i></div>
					<div class="device-card__body">
						<div class="device-card__title">麦克风 <span v-if="selectedAudioId" class="device-card__tag">已选择</span></div>
						<div class="device-card__hint">用于语音通话和通话收音</div>
						<el-select v-model="selectedAudioId" class="device-select" size="small" placeholder="使用系统默认设备" @change="saveSelection('audio', selectedAudioId)">
							<el-option v-for="device in audioOptions" :key="device.deviceId" :label="device.label" :value="device.deviceId"></el-option>
						</el-select>
					</div>
					<el-button class="device-card__action" size="mini" plain :disabled="!audioDevices.length || (testing && !microphoneTesting)" @click="testMicrophone">{{ microphoneTesting ? '停止测试' : '测试' }}</el-button>
					<div v-if="microphoneTesting || microphoneTestResult" class="device-microphone-feedback" :class="{ 'is-active': microphoneTesting, 'is-error': microphoneTestResult === 'error' }">
						<div class="device-microphone-feedback__label">{{ microphoneTestText }}</div>
						<div v-if="microphoneTesting" class="device-microphone-feedback__hint">看到音量条变化即表示已收到输入</div>
						<div class="device-microphone-meter" aria-label="麦克风输入音量">
							<i v-for="index in 24" :key="index" :class="{ 'is-on': microphoneLevel >= index * 4 }"></i>
						</div>
					</div>
				</div>
				<video v-if="previewStream" ref="preview" class="device-preview" autoplay muted playsinline></video>
			</template>
		</div>
		<span slot="footer">
			<el-button @click="dialogVisible = false">关闭</el-button>
		</span>
	</el-dialog>
</template>

<script>
import { captureError } from '@/api/camera';
import { getPreferredDeviceId, setPreferredDeviceId } from '@/utils/mediaDevices';
import { logAppEvent } from '@/utils/appLogger';

const stop = stream => stream && stream.getTracks().forEach(track => track.stop());

export default {
	name: 'DeviceCheckDialog',
	props: {
		visible: { type: Boolean, default: false },
		isMobile: { type: Boolean, default: false }
	},
	data() {
		return {
			videoDevices: [],
			audioDevices: [],
			selectedVideoId: '',
			selectedAudioId: '',
			previewStream: null,
			loading: false,
			testing: false,
			microphoneTesting: false,
			cameraTesting: false,
			microphoneLevel: 0,
			microphoneTestResult: '',
			microphoneTestText: '',
			microphoneStream: null,
			microphoneAudioContext: null,
			microphoneAnalyser: null,
			microphoneSource: null,
			microphoneFrame: 0,
			microphoneFrameMode: '',
			microphoneMeasureResolve: null,
			microphoneCaptureCancel: null,
			microphoneTestToken: 0,
			cameraTestToken: 0,
			unsupported: false,
			statusText: '尚未检测设备',
			statusType: 'info'
		};
	},
	computed: {
		dialogVisible: {
			get() { return this.visible; },
			set(value) { this.$emit('update:visible', value); }
		},
		videoOptions() { return this.videoDevices.length ? this.videoDevices : [{ deviceId: '', label: '未检测到摄像头' }]; },
		audioOptions() { return this.audioDevices.length ? this.audioDevices : [{ deviceId: '', label: '未检测到麦克风' }]; }
	},
	methods: {
		async onOpen() {
			this.selectedVideoId = getPreferredDeviceId('video');
			this.selectedAudioId = getPreferredDeviceId('audio');
			await this.refreshDevices(false);
		},
		async refreshDevices(requestPermission) {
			if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
				this.unsupported = true;
				this.statusText = '当前环境不支持设备检测';
				this.statusType = 'error';
				return;
			}
			this.loading = true;
			this.unsupported = false;
			try {
				if (requestPermission) {
					const permissionErrors = [];
					for (const constraints of [{ video: true, audio: false }, { video: false, audio: true }]) {
						try {
							const stream = await navigator.mediaDevices.getUserMedia(constraints);
							stop(stream);
						} catch (error) {
							permissionErrors.push(error);
						}
					}
					if (permissionErrors.length === 2) throw permissionErrors[0];
				}
				const devices = await navigator.mediaDevices.enumerateDevices();
				this.videoDevices = devices.filter(device => device.kind === 'videoinput').map((device, index) => this.normalizeDevice(device, index));
				this.audioDevices = devices.filter(device => device.kind === 'audioinput').map((device, index) => this.normalizeDevice(device, index));
				this.selectedVideoId = this.resolveDefaultSelection(this.selectedVideoId, this.videoDevices, 'video');
				this.selectedAudioId = this.resolveDefaultSelection(this.selectedAudioId, this.audioDevices, 'audio');
				this.statusText = `已发现 ${this.videoDevices.length} 个摄像头、${this.audioDevices.length} 个麦克风`;
				this.statusType = 'info';
			} catch (error) {
				const normalized = captureError(error, 'video');
				this.statusText = normalized.message;
				this.statusType = 'error';
			} finally {
				this.loading = false;
			}
		},
		normalizeDevice(device, index) {
			return { deviceId: device.deviceId, label: device.label || `设备 ${index + 1}` };
		},
		resolveDefaultSelection(current, devices, kind) {
			const ids = devices.map(device => device.deviceId).filter(Boolean);
			const selected = current && ids.includes(current) ? current : (ids[0] || '');
			setPreferredDeviceId(kind, selected);
			return selected;
		},
		saveSelection(kind, value) {
			setPreferredDeviceId(kind, value);
			this.statusText = '设备选择已保存，将用于下一次通话';
			this.statusType = 'info';
		},
		async testCamera() {
			if (this.cameraTesting) {
				this.stopCameraTest();
				this.testing = false;
				this.statusText = '摄像头测试已停止';
				this.statusType = 'info';
				logAppEvent('device', 'camera-test-stopped');
				return;
			}
			this.stopMicrophoneTest();
			this.stopPreview();
			this.microphoneTestResult = '';
			this.microphoneTestText = '';
			this.testing = true;
			this.cameraTesting = true;
			const testToken = ++this.cameraTestToken;
			logAppEvent('device', 'camera-test-started', { deviceIdSelected: Boolean(this.selectedVideoId) });
			try {
				const video = this.selectedVideoId ? { deviceId: { exact: this.selectedVideoId } } : true;
				const stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
				if (!this.cameraTesting || testToken !== this.cameraTestToken) {
					stop(stream);
					return;
				}
				this.previewStream = stream;
				this.statusText = '摄像头可用';
				this.statusType = 'info';
				logAppEvent('device', 'camera-test-succeeded', { deviceIdSelected: Boolean(this.selectedVideoId) });
				await this.$nextTick();
				if (this.$refs.preview) this.$refs.preview.srcObject = this.previewStream;
			} catch (error) {
				if (!this.cameraTesting || testToken !== this.cameraTestToken) return;
				this.stopCameraTest();
				this.statusText = captureError(error, 'video').message;
				this.statusType = 'error';
				logAppEvent('device', 'camera-test-failed', { errorName: error && error.name, errorMessage: error && error.message });
			} finally {
				if (!this.cameraTesting) this.testing = false;
			}
		},
		async testMicrophone() {
			if (this.microphoneTesting) {
				const level = this.microphoneLevel;
				this.stopMicrophoneTest();
				this.testing = false;
				this.statusText = '麦克风测试已停止';
				this.statusType = 'info';
				logAppEvent('device', 'microphone-test-stopped', { result: this.microphoneTestResult, level });
				return;
			}
			this.stopMicrophoneTest();
			this.stopPreview();
			this.testing = true;
			this.microphoneTesting = true;
			this.microphoneLevel = 0;
			this.microphoneTestResult = '';
			this.microphoneTestText = '请说话，输入音量会实时显示';
			this.statusText = '正在检测麦克风，请说话';
			this.statusType = 'info';
			const testToken = ++this.microphoneTestToken;
			logAppEvent('device', 'microphone-test-started', { deviceIdSelected: Boolean(this.selectedAudioId) });
			try {
				const audio = this.selectedAudioId ? { deviceId: { exact: this.selectedAudioId } } : true;
				this.microphoneStream = await this.captureMicrophoneStream({ audio, video: false }, testToken);
				if (!this.microphoneTesting || testToken !== this.microphoneTestToken) return;
				this.measureMicrophoneInput();
			} catch (error) {
				if (testToken !== this.microphoneTestToken) return;
				this.statusText = captureError(error, 'audio').message;
				this.statusType = 'error';
				this.microphoneTestResult = 'error';
				this.microphoneTestText = this.statusText;
				logAppEvent('device', 'microphone-test-failed', { errorName: error && error.name, errorMessage: error && error.message });
				this.stopMicrophoneTest();
				this.testing = false;
			}
		},
		captureMicrophoneStream(constraints, testToken) {
			return new Promise((resolve, reject) => {
				let settled = false;
				const clearCancel = () => {
					if (this.microphoneCaptureCancel === cancel) this.microphoneCaptureCancel = null;
				};
				const cancel = () => {
					if (settled) return;
					settled = true;
					clearCancel();
					reject(Object.assign(new Error('麦克风检测已取消'), { name: 'AbortError' }));
				};
				this.microphoneCaptureCancel = cancel;
				Promise.resolve().then(() => navigator.mediaDevices.getUserMedia(constraints)).then(stream => {
					if (settled || !this.microphoneTesting || testToken !== this.microphoneTestToken) {
						stop(stream);
						return;
					}
					settled = true;
					clearCancel();
					resolve(stream);
				}, error => {
					if (settled) return;
					settled = true;
					clearCancel();
					reject(error);
				});
			});
		},
		measureMicrophoneInput() {
			return new Promise(resolve => {
				let settled = false;
				const finish = () => {
					if (settled) return;
					settled = true;
					if (this.microphoneMeasureResolve === finish) this.microphoneMeasureResolve = null;
					resolve();
				};
				this.microphoneMeasureResolve = finish;
				const AudioContextClass = window.AudioContext || window.webkitAudioContext;
				if (!AudioContextClass || !this.microphoneStream) {
					this.microphoneTestResult = 'available';
					this.microphoneTestText = '麦克风已连接';
					return;
				}
				try {
					this.microphoneAudioContext = new AudioContextClass();
					const resumeContext = this.microphoneAudioContext.state === 'suspended'
						? this.microphoneAudioContext.resume().catch(() => {})
						: Promise.resolve();
					this.microphoneAnalyser = this.microphoneAudioContext.createAnalyser();
					this.microphoneAnalyser.fftSize = 512;
					this.microphoneSource = this.microphoneAudioContext.createMediaStreamSource(this.microphoneStream);
					this.microphoneSource.connect(this.microphoneAnalyser);
					const samples = new Uint8Array(this.microphoneAnalyser.fftSize);
					let detected = false;
					const sample = () => {
						if (!this.microphoneTesting || !this.microphoneAnalyser) return finish();
						this.microphoneAnalyser.getByteTimeDomainData(samples);
						let sum = 0;
						for (let index = 0; index < samples.length; index += 1) {
							const normalized = (samples[index] - 128) / 128;
							sum += normalized * normalized;
						}
						const level = Math.min(100, Math.round(Math.sqrt(sum / samples.length) * 360));
						this.microphoneLevel = level;
						if (level >= 5) {
							detected = true;
							this.microphoneTestResult = 'input';
							this.microphoneTestText = '已检测到声音输入';
						} else if (!detected) {
							this.microphoneTestResult = 'silent';
							this.microphoneTestText = '未检测到明显声音，请继续说话';
						}
						if (typeof requestAnimationFrame === 'function') {
							this.microphoneFrameMode = 'raf';
							this.microphoneFrame = requestAnimationFrame(sample);
						} else {
							this.microphoneFrameMode = 'timeout';
							this.microphoneFrame = setTimeout(sample, 33);
						}
					};
					resumeContext.then(sample);
				} catch (_) {
					this.microphoneTestResult = 'available';
					this.microphoneTestText = '麦克风已连接';
				}
			});
		},
		stopMicrophoneTest() {
			this.microphoneTestToken += 1;
			if (this.microphoneCaptureCancel) this.microphoneCaptureCancel();
			this.microphoneCaptureCancel = null;
			if (this.microphoneMeasureResolve) this.microphoneMeasureResolve();
			this.microphoneMeasureResolve = null;
			if (this.microphoneFrame) {
				if (this.microphoneFrameMode === 'timeout') clearTimeout(this.microphoneFrame);
				else if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this.microphoneFrame);
			}
			this.microphoneFrame = 0;
			this.microphoneFrameMode = '';
			if (this.microphoneSource) {
				try { this.microphoneSource.disconnect(); } catch (_) { /* ignored */ }
			}
			this.microphoneSource = null;
			this.microphoneAnalyser = null;
			if (this.microphoneAudioContext) this.microphoneAudioContext.close().catch(() => {});
			this.microphoneAudioContext = null;
			stop(this.microphoneStream);
			this.microphoneStream = null;
			this.microphoneTesting = false;
			this.microphoneLevel = 0;
		},
		stopCameraTest() {
			this.cameraTestToken += 1;
			this.stopPreview();
			this.cameraTesting = false;
		},
		stopPreview() {
			stop(this.previewStream);
			this.previewStream = null;
		},
		stopTests() {
			this.stopCameraTest();
			this.stopMicrophoneTest();
			this.stopPreview();
			this.testing = false;
		}
	}
};
</script>

<style scoped lang="scss">
.device-check-dialog {
	.device-check-intro {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 4px 2px 16px;
		border-bottom: 1px solid var(--im-surface-panel-border);
	}

	.device-check-intro__icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 40px;
		height: 40px;
		border-radius: 12px;
		background: rgba(var(--im-primary-rgb), 0.1);
		color: var(--im-color-primary);
		font-size: 19px;
		flex-shrink: 0;
	}

	.device-check-intro__copy {
		flex: 1;
		min-width: 0;
	}

	.device-check-intro__title {
		font-size: 15px;
		font-weight: 600;
		color: var(--im-text-color-primary);
	}

	.device-check-intro__text,
	.device-card__hint {
		color: var(--im-text-color-light);
		font-size: 12px;
		line-height: 1.5;
	}

	.device-check-intro__text {
		margin-top: 3px;
	}

	.device-check-summary {
		display: flex;
		align-items: center;
		gap: 7px;
		margin: 14px 0 10px;
		padding: 9px 11px;
		border-radius: 8px;
		background: rgba(var(--im-success-rgb, 76, 175, 80), 0.08);
		color: var(--im-color-success);
		font-size: 12px;

		&.is-error {
			background: rgba(var(--im-danger-rgb, 245, 108, 108), 0.08);
			color: var(--im-color-danger);
		}
	}

	.device-card {
		display: grid;
		grid-template-columns: 38px minmax(0, 1fr) auto;
		align-items: center;
		gap: 10px;
		padding: 12px;
		margin-bottom: 10px;
		border: 1px solid var(--im-surface-panel-border);
		border-radius: 10px;
		background: var(--im-control-soft-bg);
	}

	.device-card__icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 34px;
		height: 34px;
		border-radius: 9px;
		font-size: 17px;

		&--camera {
			background: rgba(var(--im-primary-rgb), 0.1);
			color: var(--im-color-primary);
		}

		&--audio {
			background: rgba(245, 158, 11, 0.12);
			color: #b45309;
		}
	}

	.device-card__body {
		min-width: 0;
	}

	.device-card__title {
		display: flex;
		align-items: center;
		gap: 6px;
		font-size: 13px;
		font-weight: 600;
		color: var(--im-text-color-primary);
	}

	.device-card__tag {
		padding: 2px 5px;
		border-radius: 4px;
		background: rgba(var(--im-primary-rgb), 0.1);
		color: var(--im-color-primary);
		font-size: 10px;
		font-weight: 500;
	}

	.device-select {
		width: 100%;
		margin-top: 7px;

		::v-deep .el-input__inner { background: var(--im-chat-bg); }
	}

	.device-card__action {
		align-self: end;
		width: 84px;
		min-width: 84px;
		height: 32px;
		min-height: 32px;
		margin-top: 7px;
		padding: 0 12px;
		line-height: 30px;
		white-space: nowrap;
	}

	.device-preview {
		display: block;
		width: 100%;
		max-height: 220px;
		margin-top: 12px;
		border-radius: 8px;
		background: #121923;
		object-fit: contain;
	}

	.device-microphone-feedback {
		grid-column: 2 / -1;
		margin-top: 8px;
		padding: 7px 9px;
		border-radius: 6px;
		background: var(--im-chat-bg);
		color: var(--im-text-color-light);
		font-size: 11px;

		&.is-active { color: var(--im-color-primary); }
		&.is-error { color: var(--im-color-danger); }
	}

	.device-microphone-feedback__label { line-height: 1.4; }
	.device-microphone-feedback__hint {
		margin-top: 2px;
		color: var(--im-text-color-light);
		font-size: 10px;
	}

	.device-microphone-meter {
		display: flex;
		align-items: end;
		gap: 3px;
		width: 100%;
		height: 14px;
		margin-top: 5px;

		i {
			display: block;
			flex: 1 1 0;
			min-width: 2px;
			height: 5px;
			border-radius: 2px;
			background: var(--im-surface-panel-border);
			transition: height 80ms ease, background 80ms ease;

			&:nth-child(2n) { height: 8px; }
			&:nth-child(3n) { height: 11px; }
			&.is-on { background: var(--im-color-primary); }
		}
	}

	.device-check-empty {
		padding: 24px 0;
		color: var(--im-text-color-light);
		text-align: center;
	}
}
</style>
